import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Globe, UserPlus, Check, Trash2, Mail, Phone, Calendar, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface RegistrationNotification {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  registered_at: string;
  is_read: boolean;
  created_at: string;
}

const RegistrationNotifications = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const queryClient = useQueryClient();

  const { data: notifications, isLoading } = useQuery({
    queryKey: ["registration-notifications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("registration_notifications")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as RegistrationNotification[];
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("registration_notifications").update({ is_read: true }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["registration-notifications"] });
      toast.success(isRTL ? "تم تحديث الإشعار" : "Notification updated");
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("registration_notifications").update({ is_read: true }).eq("is_read", false);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["registration-notifications"] });
      toast.success(isRTL ? "تم تحديث جميع الإشعارات" : "All notifications marked as read");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("registration_notifications").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["registration-notifications"] });
      toast.success(isRTL ? "تم حذف الإشعار" : "Notification deleted");
    },
  });

  const unreadCount = notifications?.filter((n) => !n.is_read).length || 0;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "PPpp", { locale: isRTL ? ar : enUS });
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/admin" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
            </Link>
            <Button variant="ghost" size="icon" onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")} className="rounded-full text-white/70 hover:text-white hover:bg-white/10">
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <UserPlus className="w-6 h-6 text-primary" />
            <h1 className="text-3xl font-bold text-white">
              {isRTL ? "إشعارات التسجيل" : "Registration Notifications"}
            </h1>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="text-sm">{unreadCount} {isRTL ? "جديد" : "new"}</Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={() => markAllAsReadMutation.mutate()} disabled={markAllAsReadMutation.isPending} className="border-white/20 text-white hover:bg-white/10">
              <Check className="w-4 h-4 mr-2" />
              {isRTL ? "تحديد الكل كمقروء" : "Mark All as Read"}
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : notifications && notifications.length > 0 ? (
          <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/15 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-white/5">
                  <TableHead className="text-white/70">{isRTL ? "الحالة" : "Status"}</TableHead>
                  <TableHead className="text-white/70">{isRTL ? "الاسم" : "Name"}</TableHead>
                  <TableHead className="text-white/70">{isRTL ? "البريد الإلكتروني" : "Email"}</TableHead>
                  <TableHead className="text-white/70">{isRTL ? "الهاتف" : "Phone"}</TableHead>
                  <TableHead className="text-white/70">{isRTL ? "تاريخ التسجيل" : "Registration Date"}</TableHead>
                  <TableHead className="text-white/70">{isRTL ? "الإجراءات" : "Actions"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notifications.map((notification) => (
                  <TableRow key={notification.id} className={`border-white/10 hover:bg-white/5 ${notification.is_read ? "opacity-60" : ""}`}>
                    <TableCell>
                      {notification.is_read ? (
                        <Badge variant="secondary"><EyeOff className="w-3 h-3 mr-1" />{isRTL ? "مقروء" : "Read"}</Badge>
                      ) : (
                        <Badge variant="default" className="bg-green-500"><Eye className="w-3 h-3 mr-1" />{isRTL ? "جديد" : "New"}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-white">{notification.full_name}</TableCell>
                    <TableCell>
                      <a href={`mailto:${notification.email}`} className="flex items-center gap-1 text-blue-400 hover:underline">
                        <Mail className="w-4 h-4" />{notification.email}
                      </a>
                    </TableCell>
                    <TableCell>
                      {notification.phone ? (
                        <a href={`tel:${notification.phone}`} className="flex items-center gap-1 text-green-400 hover:underline">
                          <Phone className="w-4 h-4" />{notification.phone}
                        </a>
                      ) : (
                        <span className="text-white/30">{isRTL ? "غير محدد" : "Not provided"}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-white/50">
                        <Calendar className="w-4 h-4" />{formatDate(notification.registered_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {!notification.is_read && (
                          <Button variant="ghost" size="sm" onClick={() => markAsReadMutation.mutate(notification.id)} disabled={markAsReadMutation.isPending} className="text-white/70 hover:text-white hover:bg-white/10">
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{isRTL ? "تأكيد الحذف" : "Confirm Delete"}</AlertDialogTitle>
                              <AlertDialogDescription>{isRTL ? "هل أنت متأكد من حذف هذا الإشعار؟" : "Are you sure you want to delete this notification?"}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{isRTL ? "إلغاء" : "Cancel"}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteMutation.mutate(notification.id)} className="bg-red-500 hover:bg-red-600">{isRTL ? "حذف" : "Delete"}</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12 bg-white/10 backdrop-blur-md rounded-lg border border-white/15">
            <UserPlus className="w-12 h-12 mx-auto text-white/30 mb-4" />
            <h3 className="text-lg font-medium text-white/50">
              {isRTL ? "لا توجد إشعارات تسجيل" : "No registration notifications"}
            </h3>
            <p className="text-sm text-white/30 mt-2">
              {isRTL ? "ستظهر هنا إشعارات الأعضاء الجدد عند التسجيل" : "New member notifications will appear here when they register"}
            </p>
          </div>
        )}
      </main>
    </AdminPageBackground>
  );
};

export default RegistrationNotifications;
